import React, { useState, useEffect } from "react";
import axios from "axios";
import { Modal, Alert, Button, Typography } from "antd";
import { MailOutlined } from "@ant-design/icons";
import { sendPasswordResetLink, type PasswordResetLink } from "../../api/admin/userApi";
import { formatLocalDateTime } from "../../utils/userPrefs/DateTimeUtils";
import { User } from "../../types/admin/UserTypes";

const { Paragraph } = Typography;

export const NO_DELIVERABLE_EMAIL_MESSAGE =
	"This account has no email address that can receive a reset link. Add a valid email to the profile first.";

interface ResetPasswordModalProps {
	isOpen: boolean;
	onClose: () => void;
	user: Pick<User, "id"> & Partial<Pick<User, "email">>;
	handleErrors: (error: unknown) => void;
}

// Staff never choose a password for someone else: identity emails the user a single-use reset link (contract §15 row 63).
const ResetPasswordModal: React.FC<ResetPasswordModalProps> = ({
	isOpen,
	onClose,
	user,
	handleErrors,
}) => {
	const [sent, setSent] = useState<PasswordResetLink | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		if (isOpen) {
			setSent(null);
			setError(null);
		}
	}, [isOpen]);

	const handleSend = async () => {
		setLoading(true);
		setError(null);
		try {
			setSent(await sendPasswordResetLink(user.id));
		} catch (err) {
			if (axios.isAxiosError(err) && err.response?.status === 409) {
				setError(NO_DELIVERABLE_EMAIL_MESSAGE);
			} else {
				handleErrors(err);
				onClose();
			}
		} finally {
			setLoading(false);
		}
	};

	return (
		<Modal
			title="Reset Password"
			open={isOpen}
			onCancel={onClose}
			destroyOnClose
			footer={
				sent
					? [<Button key="close" type="primary" onClick={onClose}>Close</Button>]
					: [
						<Button key="cancel" onClick={onClose} disabled={loading}>Cancel</Button>,
						<Button key="submit" type="primary" icon={<MailOutlined />} loading={loading} onClick={handleSend}>
							Send reset link
						</Button>,
					]
			}
		>
			<Paragraph>
				{user.email
					? `A single-use link to set a new password will be emailed to ${user.email}.`
					: "A single-use link to set a new password will be emailed to this account's address."}
			</Paragraph>
			{sent && (
				<Alert
					type="success"
					showIcon
					message="Reset link sent"
					description={sent.expires_at ? `The link expires ${formatLocalDateTime(sent.expires_at)}.` : undefined}
				/>
			)}
			{error && <Alert type="error" showIcon message={error} />}
		</Modal>
	);
};

export default ResetPasswordModal;
